'use client';

import { withBase } from '../lib/brand';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Entity } from '../types';
import type { OwnershipProvider } from '../utils/ownershipAuth';
import { ownershipApiUrl } from '../utils/ownershipApi';
import {
  EMPTY_CLAIM_FORM,
  clearPendingClaim,
  readPendingClaim,
  savePendingClaim,
  type ClaimFormData,
} from '../lib/claimDraft';

interface ClaimSiteButtonProps {
  entity: Entity;
  className?: string;
}

interface ClaimModalProps {
  entity: Entity;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (claimId: string) => void;
  confirmedClaimId?: string;
}

// Remembers, per entity, that this browser already lodged a claim. Without it
// a reload brings back the plain "Claim This Site" button and the operator
// concludes the claim vanished (Maitland Showground, 2026-09-28 — the claim
// was in the graph; the operator told the chat assistant it had disappeared).
const SUBMITTED_KEY = (entityId: string | number) => `claimSubmitted:${entityId}`;

function readSubmittedClaim(entityId: string | number): string | null {
  try {
    return localStorage.getItem(SUBMITTED_KEY(entityId));
  } catch {
    return null;
  }
}

function rememberSubmittedClaim(entityId: string | number, claimId: string): void {
  try {
    localStorage.setItem(SUBMITTED_KEY(entityId), claimId || 'submitted');
  } catch {
    // Storage blocked — the in-page confirmation still shows.
  }
}


// The modal is portalled onto <body>. Rendered inline it inherited whatever
// containing block its parent made: the store campsite page puts the button
// inside section.ca-more, whose transform turns `position: fixed` into
// "fixed to that section", so the dialog was clipped and the next card
// (campervan hire) painted over Submit Claim and Cancel.
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const ModalShell: React.FC<{ onClose: () => void; labelledBy: string; children: React.ReactNode }> = ({ onClose, labelledBy, children }) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const html = document.documentElement;
    const prevOverflow = html.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    html.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>('input, select, textarea') || panel;
    first?.focus({ preventScroll: true });

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const head = items[0];
      const tail = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === head || !panelRef.current.contains(active))) {
        e.preventDefault();
        tail.focus();
      } else if (!e.shiftKey && (active === tail || !panelRef.current.contains(active))) {
        e.preventDefault();
        head.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      html.style.overflow = prevOverflow;
      document.body.style.overflow = prevBodyOverflow;
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
    };
  }, []);

  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      className="claim-modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: 10100 }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="claim-modal"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto outline-none"
        style={{ overscrollBehavior: 'contain' }}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
};

const ClaimConfirmation: React.FC<{ claimId: string; onClose: () => void }> = ({ claimId, onClose }) => (
  <div className="p-6 space-y-4">
    <h2 id="claim-modal-title" className="text-2xl font-bold text-gray-900">We have your claim</h2>
    <p className="text-gray-700">
      Nothing was lost. We&rsquo;ve emailed you a copy, and we check every claim by hand,
      usually within two business days.
    </p>
    <p className="text-gray-700">
      You can follow it in your owner portal, under Claims.
    </p>
    {claimId && claimId !== 'submitted' && (
      <p className="text-sm text-gray-500">Reference: {claimId}</p>
    )}
    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
      >
        Close
      </button>
      <a
        href={withBase('/owner-portal')}
        className="px-6 py-2 bg-accent text-white rounded-md hover:bg-[#1558b0]"
      >
        Open owner portal
      </a>
    </div>
  </div>
);

const ClaimModal: React.FC<ClaimModalProps> = ({ entity, isOpen, onClose, onSuccess, confirmedClaimId }) => {
  const [formData, setFormData] = useState<ClaimFormData>(EMPTY_CLAIM_FORM);
  const [restoredDraft, setRestoredDraft] = useState(false);
  const [evidenceFiles, setEvidenceFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showAuthOptions, setShowAuthOptions] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setErrors({});
      setShowAuthOptions(false);
      setIsSubmitting(false);
      return;
    }
    // Re-opening after a sign-in round trip: put the operator's typed claim
    // back rather than making them start again.
    const pending = readPendingClaim(entity.id);
    if (pending) {
      setFormData({ ...EMPTY_CLAIM_FORM, ...pending.form });
      setRestoredDraft(true);
    }
  }, [isOpen, entity.id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setEvidenceFiles(prev => [...prev, ...files].slice(0, 5)); // Max 5 files
  };

  const removeFile = (index: number) => {
    setEvidenceFiles(prev => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.contact_phone.trim()) {
      newErrors.contact_phone = 'Contact phone is required';
    }
    if (!formData.contact_email.trim()) {
      newErrors.contact_email = 'Contact email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
      newErrors.contact_email = 'Please enter a valid email address';
    }
    if (!formData.evidence_description.trim()) {
      newErrors.evidence_description = 'Please provide a description';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const redirectToOwnerPortal = (provider?: OwnershipProvider) => {
    if (typeof window === 'undefined') return;
    const currentUrl = window.location.href;
    const params = new URLSearchParams();
    params.set('login', '1');
    params.set('return', currentUrl);
    if (provider) params.set('provider', provider);
    window.location.href = withBase(`/owner-portal?${params.toString()}`);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Get JWT token from localStorage
      const token = localStorage.getItem('ownership_token');
      
      if (!token) {
        // Stash BEFORE the redirect — see PENDING_CLAIM_KEY above.
        savePendingClaim(entity.id, formData);
        setShowAuthOptions(true);
        setErrors({
          submit: evidenceFiles.length > 0
            ? 'Please sign in to claim this site — we need to verify your ownership before submitting. Your details are saved, but you will need to attach your files again after signing in.'
            : 'Please sign in to claim this site. We need to verify your ownership before submitting — your details are saved and will be waiting when you come back.',
        });
        setIsSubmitting(false);
        redirectToOwnerPortal();
        return;
      }

      // Prepare form data
      const submitData = {
        ...formData,
        entity_id: entity.id,
        evidence: [formData.evidence_description],
        evidence_files: evidenceFiles.length > 0 ? await Promise.all(
          evidenceFiles.map(async (file) => ({
            filename: file.name,
            content_type: file.type,
            content: await fileToBase64(file)
          }))
        ) : []
      };

      const response = await fetch(ownershipApiUrl('/claims'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      const result = await response.json();

      if (result.success) {
        clearPendingClaim();
        onSuccess(result.claim.claim_id);
      } else {
        setErrors({ submit: result.error || 'Failed to submit claim' });
      }
    } catch (error) {
      console.error('Error submitting claim:', error);
      setErrors({ submit: 'An error occurred while submitting your claim' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result as string;
        resolve(base64.split(',')[1]); // Remove data:mime;base64, prefix
      };
      reader.onerror = reject;
    });
  };

  const loginOptions = [
    {
      provider: 'google' as const,
      label: 'Continue with Google',
      classes: 'bg-green-600 hover:bg-green-700 text-white',
      icon: (
        <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
      ),
    },
    {
      provider: 'facebook' as const,
      label: 'Continue with Facebook',
      classes: 'bg-accent hover:bg-[#1558b0] text-white',
      icon: (
        <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
          <path d="M22 12a10 10 0 10-11.54 9.87v-6.99H7.9V12h2.56V9.8c0-2.53 1.5-3.94 3.8-3.94 1.1 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.88h-2.34v6.99A10 10 0 0022 12z" />
        </svg>
      ),
    },
    {
      provider: 'shopify' as const,
      label: 'Continue with Shopify',
      classes: 'bg-[#95BF47] hover:bg-[#5E8E3E] text-white',
      icon: (
        <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
          <path d="M21.5 4.24c-.05-.38-.36-.68-.74-.72-.54-.05-5.33-.39-5.33-.39s-.89-.84-1.31-1.2c-.42-.36-1.23-.31-1.23-.31l-.74 1.86s-.9.27-1.49.43c-.59.16-1.28.52-1.59.78-.31.27-.44.55-.44.55l-2.09.26s-.64.07-.74.74c-.1.67-1.53 12.3-1.53 12.3s-.12.82.72.92c.84.1 4.51.61 4.51.61l1.16 1.79s.28.44.86.44h7.4c.71 0 .86-.59.86-.59s1.88-13.78 1.94-14.16c.06-.38-.2-.71-.2-.71L21.5 4.24zM11.11 5.9l1.39-.18.57-1.43s.09-.27.31-.27c.22 0 .42.21.42.21l.99.93-1.65.21-1.41.53-.62-.01zm1.73 9.89c-.57 0-1.09-.15-1.54-.42l-.17.97h-.96l1.1-6.2c.44-.28.98-.44 1.6-.44 1.34 0 2.16.84 2.16 2.18 0 1.52-.99 2.91-2.19 2.91zm-.25-4.38c-.21 0-.4.04-.57.11l-.44 2.47c.17.1.37.16.59.16.67 0 1.24-.78 1.24-1.73-.01-.62-.31-1.01-.82-1.01z" />
        </svg>
      ),
    },
  ];

  if (!isOpen) return null;

  if (confirmedClaimId) {
    return (
      <ModalShell onClose={onClose} labelledBy="claim-modal-title">
        <ClaimConfirmation claimId={confirmedClaimId} onClose={onClose} />
      </ModalShell>
    );
  }

  return (
    <ModalShell onClose={() => { if (!isSubmitting) onClose(); }} labelledBy="claim-modal-title">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 id="claim-modal-title" className="text-2xl font-bold text-gray-900">Claim This {(entity.entity_type === 'accom' || entity.entity_type === 'accommodation') ? 'Property' : 'Campsite'}</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="text-gray-400 hover:text-gray-600"
              disabled={isSubmitting}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mb-6 p-4 bg-[#e3f2fd] border border-[#e3f2fd] rounded-lg">
            <h3 className="font-semibold text-[#123f7d]">Claiming: {entity.name}</h3>
            <p className="text-[#1558b0] text-sm mt-1">
              By claiming this campsite, you'll be able to manage its listing, update information, 
              and respond to reviews.
            </p>
          </div>

          {restoredDraft && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <h3 className="font-semibold text-green-900">We kept what you typed</h3>
              <p className="text-green-800 text-sm mt-1">
                Your claim details are back below. Check them over, re-attach any
                evidence files, then submit — nothing has been sent yet.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Business Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Name
                  </label>
                  <input
                    type="text"
                    name="business_name"
                    value={formData.business_name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Your campsite's business name (optional)"
                  />
                  {errors.business_name && (
                    <p className="text-red-600 text-sm mt-1">{errors.business_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Registration Number
                  </label>
                  <input
                    type="text"
                    name="business_registration"
                    value={formData.business_registration}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="ABN or business registration number"
                  />
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Phone *
                  </label>
                  <input
                    type="tel"
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Your business phone number"
                  />
                  {errors.contact_phone && (
                    <p className="text-red-600 text-sm mt-1">{errors.contact_phone}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Email *
                  </label>
                  <input
                    type="email"
                    name="contact_email"
                    value={formData.contact_email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="your.email@domain.com"
                  />
                  {errors.contact_email && (
                    <p className="text-red-600 text-sm mt-1">{errors.contact_email}</p>
                  )}
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website URL
                </label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="https://your-campsite-website.com"
                />
              </div>
            </div>

            {/* Ownership Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Ownership Information</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Relationship to Site
                </label>
                <select
                  name="relationship_to_site"
                  value={formData.relationship_to_site}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="owner">Owner</option>
                  <option value="manager">Manager</option>
                  <option value="authorized_representative">Authorized Representative</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  name="evidence_description"
                  value={formData.evidence_description}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Please describe your relationship to this campsite and why you should manage this listing"
                />
                {errors.evidence_description && (
                  <p className="text-red-600 text-sm mt-1">{errors.evidence_description}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Supporting Documents (Optional, max 5 files)
                </label>
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={handleFileChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <p className="text-gray-500 text-sm mt-1">
                  Optionally upload any supporting documents (business license, property deed, etc.)
                </p>
                
                {evidenceFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {evidenceFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-sm text-gray-700">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Additional Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Message
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Any additional information you'd like to provide..."
              />
            </div>

            {/* Error Display */}
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 space-y-3">
                <p className="text-red-700 text-sm">{errors.submit}</p>
                {showAuthOptions && (
                  <div className="space-y-2">
                    <p className="text-xs text-red-600 font-medium">
                      Choose a sign-in option below, then try submitting again:
                    </p>
                    <div className="grid gap-2 sm:grid-cols-3">
                      {loginOptions.map((option) => (
                        <button
                          key={option.provider}
                          type="button"
                          onClick={() => redirectToOwnerPortal(option.provider)}
                          className={`flex items-center justify-center rounded-md px-3 py-2 text-xs font-semibold transition ${option.classes}`}
                        >
                          {option.icon}
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Submit Claim'
                )}
              </button>
            </div>
          </form>
        </div>
    </ModalShell>
  );
};

const ClaimSiteButton: React.FC<ClaimSiteButtonProps> = ({ entity, className = '' }) => {
  const [showModal, setShowModal] = useState(false);
  const [claimSubmitted, setClaimSubmitted] = useState(false);
  const [claimId, setClaimId] = useState<string>('');

  // Coming back from the owner-portal sign-in lands here with the modal closed
  // and no sign anything was in flight. Re-open it so the operator finishes the
  // claim they already started instead of assuming it went through.
  useEffect(() => {
    const previous = readSubmittedClaim(entity.id);
    if (previous) {
      setClaimId(previous);
      setClaimSubmitted(true);
      return;
    }
    if (readPendingClaim(entity.id)) {
      setShowModal(true);
    }
  }, [entity.id]);

  // Keep the modal open on success and swap the form for the confirmation.
  // Closing it the instant the POST returned left a small card in a sidebar
  // that, on a phone, sat well below the fold — so the page looked untouched.
  const handleClaimSuccess = (submittedClaimId: string) => {
    rememberSubmittedClaim(entity.id, submittedClaimId);
    setClaimId(submittedClaimId);
    setClaimSubmitted(true);
  };

  if (claimSubmitted && !showModal) {
    return (
      <div className={`bg-green-50 border border-green-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-start">
          <svg className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <div>
            <h4 className="text-green-900 font-semibold">Claim received</h4>
            <p className="text-green-700 text-sm">
              We check every claim by hand and will email you when it&rsquo;s decided.{' '}
              <a href={withBase('/owner-portal')} className="underline font-medium">
                Follow it in your owner portal
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className={`bg-accent text-white px-4 py-2 rounded-lg hover:bg-[#1558b0] transition-colors flex items-center ${className}`}
      >
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        Claim This Site
      </button>

      <ClaimModal
        entity={entity}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={handleClaimSuccess}
        confirmedClaimId={claimSubmitted ? claimId || 'submitted' : undefined}
      />
    </>
  );
};

export default ClaimSiteButton;

